import { notification } from '@surecompanies/core_components'
import {
  useQueryClient,
  useMutation,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { Dayjs } from 'dayjs'
import client from 'enrollment-platform/api/eps.client'
import { useState } from 'react'
import { toCalendarDate } from '../utils/toCalendarDate'

type EnrollmentFormData = {
  id: string
  benefitEffectivePolicy: string
  enrollmentDates: Dayjs[]
  benefitEffectiveDate: string
}

export type EnrollmentConfiguration = {
  id: string | number
  benefitEffectivePolicy: number
  benefitEffectivePolicyUnit: string
  benefitEndDate: string
  enrollmentEndDate: string
  enrollmentStartDate: string
  benefitEffectiveDate: string
  isEditable: boolean
  isPaused?: boolean
}

// Shape of the pause/resume response fields the UI reads. The endpoints also
// return id/isPaused (and clearRunId on a deferred resume), but those are not
// read here — the config query is invalidated to refetch instead.
type ConfigActionResponse = {
  reminderEmail?: { updated: boolean; message?: string }
}

type EnrollmentConfigurationResponse = {
  isCreating: boolean
  isLoading: boolean
  getEnrollmentConfigurationQuery: UseQueryResult<
    EnrollmentConfiguration[],
    Error
  >
  createOrUpdateEnrollmentConfiguration: (
    enrollmentFormData: EnrollmentFormData
  ) => void
  onNewEnrollmentConfiguration: () => void
  isCreatingOrUpdating: boolean
  selectedEnrollmentId: string | null
  setSelectedEnrollmentId: (id: string | null) => void
  pauseEnrollment: (
    enrollmentId: string,
    options?: { onSuccess?: () => void }
  ) => void
  isPausing: boolean
  resumeEnrollment: (
    variables: { enrollmentId: string; clearCarts: boolean },
    options?: { onSuccess?: () => void; onError?: (error: any) => void }
  ) => void
  isResuming: boolean
}

export default function useEnrollmentConfiguration(
  id: string
): EnrollmentConfigurationResponse {
  const queryClient = useQueryClient()
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<
    string | null
  >(null)

  async function getEnrollmentConfiguration(): Promise<
    EnrollmentConfiguration[]
  > {
    try {
      const { data } = await client.get<EnrollmentConfiguration[]>(
        `/customers/${id}/configuration/enrollment`,
        {
          params: {
            sortBy: 'benefitEffectiveDate',
            orderBy: 'desc',
          },
        }
      )
      return data
    } catch (error: any) {
      notification.error({
        message: 'Failed to get enrollment configuration',
        description: error?.message ?? 'Unknown error',
      })
      return []
    }
  }

  const createOrUpdateEnrollmentConfiguration = async (
    enrollmentFormData: EnrollmentFormData
  ) => {
    const enrollmentItem = {
      ...(enrollmentFormData.id ? { id: enrollmentFormData.id } : {}),
      enrollmentStartDate: toCalendarDate(
        enrollmentFormData.enrollmentDates[0]
      ),
      enrollmentEndDate: toCalendarDate(enrollmentFormData.enrollmentDates[1]),
      benefitEffectiveDate: toCalendarDate(
        enrollmentFormData.benefitEffectiveDate
      ),
      benefitEffectivePolicy: Number(enrollmentFormData.benefitEffectivePolicy),
      benefitEffectivePolicyUnit: 'Days',
    } as EnrollmentConfiguration

    ;(await enrollmentItem.id)
      ? updateEnrollmentConfigurationMutation(enrollmentItem)
      : mutate(enrollmentItem)
  }

  const createEnrollmentConfiguration = async (
    enrollmentConfiguration: EnrollmentConfiguration
  ) => {
    try {
      const { data } = await client.post(
        `/customers/${id}/configuration/enrollment`,
        enrollmentConfiguration
      )
      setIsCreating(false)
      notification.success({
        message: 'Enrollment configuration successfully created',
      })
      queryClient.invalidateQueries({
        queryKey: ['getEnrollmentConfiguration', id],
      })
      return data
    } catch (error: any) {
      notification.error({
        message: 'Failed to create enrollment configuration',
        description: error?.message ?? 'Unknown error',
      })
    }
  }

  const updateEnrollmentConfiguration = async (
    enrollmentConfiguration: EnrollmentConfiguration
  ) => {
    try {
      const { data } = await client.put(
        `/customers/${id}/configuration/enrollment/${enrollmentConfiguration.id}`,
        enrollmentConfiguration
      )
      notification.success({
        message: 'Enrollment configuration successfully updated',
      })
      setSelectedEnrollmentId(null)
      queryClient.invalidateQueries({
        queryKey: ['getEnrollmentConfiguration', id],
      })
      return data
    } catch (error: any) {
      notification.error({
        message: 'Failed to update enrollment configuration',
        description: error?.message ?? 'Unknown error',
      })
    }
  }

  const getEnrollmentConfigurationQuery = useQuery({
    queryKey: ['getEnrollmentConfiguration', id],
    queryFn: () => getEnrollmentConfiguration(),
    staleTime: 0, // Data is considered stale immediately
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes after becoming inactive
  })

  const { mutate, isPending: isCreatingEnrollmentConfiguration } = useMutation({
    mutationFn: createEnrollmentConfiguration,
    mutationKey: ['createEnrollmentConfiguration', id],
  })

  const {
    mutate: updateEnrollmentConfigurationMutation,
    isPending: isEditing,
  } = useMutation({
    mutationFn: updateEnrollmentConfiguration,
    mutationKey: ['updateEnrollmentConfiguration', id],
  })

  // When a pause/resume succeeds but the reminder-email toggle did not, surface a
  // non-fatal warning — the action itself still worked, so it must not read as a failure.
  // The copy is action-specific: a paused enrollment should suppress reminders, a
  // resumed one should restore them. The backend reason (if any) is appended.
  const warnIfReminderEmailNotUpdated = (
    response: AxiosResponse<ConfigActionResponse>,
    action: 'pause' | 'resume'
  ) => {
    const reminderEmail = response?.data?.reminderEmail
    if (reminderEmail?.updated !== false) return

    const detail =
      action === 'pause'
        ? 'Reminder emails could not be turned off for this paused enrollment, so employees may still receive them.'
        : 'Reminder emails could not be turned back on for this resumed enrollment, so they may stay suppressed.'

    notification.warning({
      message:
        action === 'pause'
          ? 'Reminder emails not suppressed'
          : 'Reminder emails not restored',
      description: reminderEmail.message
        ? `${detail} (${reminderEmail.message})`
        : detail,
    })
  }

  // Shared success/error handling for the pause/resume config mutations: refresh the
  // config query on success, and surface a notification either way.
  const configActionCallbacks = (
    successMessage: string,
    errorMessage: string
  ) => ({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getEnrollmentConfiguration', id],
      })
      notification.success({ message: successMessage })
    },
    onError: (error: any) =>
      notification.error({
        message: errorMessage,
        description: error?.message ?? 'Unknown error',
      }),
  })

  const { mutate: pauseEnrollment, isPending: isPausing } = useMutation({
    mutationFn: (enrollmentId: string) =>
      client.post<ConfigActionResponse>(
        `/customers/${id}/configuration/enrollment/${enrollmentId}/pause`
      ),
    mutationKey: ['pauseEnrollment', id],
    ...configActionCallbacks('Enrollment paused', 'Failed to pause enrollment'),
    // Own body-aware onSuccess (overrides the shared one) so the reminder-email
    // outcome in the response can be surfaced; onError stays shared.
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['getEnrollmentConfiguration', id],
      })
      notification.success({ message: 'Enrollment paused' })
      warnIfReminderEmailNotUpdated(data, 'pause')
    },
  })

  const { mutate: resumeEnrollment, isPending: isResuming } = useMutation({
    mutationFn: ({
      enrollmentId,
      clearCarts,
    }: {
      enrollmentId: string
      clearCarts: boolean
    }) =>
      client.post<ConfigActionResponse>(
        `/customers/${id}/configuration/enrollment/${enrollmentId}/resume`,
        { clearCarts }
      ),
    mutationKey: ['resumeEnrollment', id],
    ...configActionCallbacks(
      'Enrollment resumed',
      'Failed to resume enrollment'
    ),
    // On a successful resume-with-clear, also refetch the clear status so the
    // newly started run's Clearing progress shows and polling begins — the shared
    // callback only invalidates the config query, and the clear-status query
    // otherwise stays parked (its refetchInterval is off while status !== Clearing).
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['getEnrollmentConfiguration', id],
      })
      if (variables.clearCarts) {
        queryClient.invalidateQueries({
          queryKey: ['getClearStatus', id, variables.enrollmentId],
        })
        notification.info({
          message: 'Clearing carts',
          description:
            'Employee carts are being cleared; the enrollment will resume automatically when this finishes.',
        })
        return
      }
      notification.success({ message: 'Enrollment resumed' })
      warnIfReminderEmailNotUpdated(data, 'resume')
    },
    // A 409 means a cart-clear is already running for this enrollment; surface it
    // as informational rather than a hard failure, and refresh the clear status.
    onError: (error: any, variables) => {
      if (error?.response?.status === 409) {
        notification.info({
          message: 'Cart clear already in progress',
          description:
            'A cart-clear is already running for this enrollment; its status is shown on the enrollment card.',
        })
        queryClient.invalidateQueries({
          queryKey: ['getEnrollmentConfiguration', id],
        })
        queryClient.invalidateQueries({
          queryKey: ['getClearStatus', id, variables.enrollmentId],
        })
        return
      }
      notification.error({
        message: 'Failed to resume enrollment',
        description: error?.message ?? 'Unknown error',
      })
    },
  })

  const onNewEnrollmentConfiguration = () => {
    setIsCreating((prev) => !prev)
  }

  const isLoading = getEnrollmentConfigurationQuery.isLoading

  return {
    isLoading,
    isCreating,
    isCreatingOrUpdating: isCreatingEnrollmentConfiguration || isEditing,
    onNewEnrollmentConfiguration,
    selectedEnrollmentId,
    setSelectedEnrollmentId,
    createOrUpdateEnrollmentConfiguration,
    getEnrollmentConfigurationQuery,
    pauseEnrollment,
    isPausing,
    resumeEnrollment,
    isResuming,
  }
}
